/*
 * /patient/address (page)
 */
import type { Address } from "@emr/db/client"
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Container,
	Page,
	PageHeader,
	toast,
} from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { AddressForm } from "../../features/address/address-form"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/patient/address")({
	component: PatientAddressPage,
	loader: rolePageLoader(["patient"]),
})

function PatientAddressPage() {
	const { t } = useTranslation()
	const [editingId, setEditingId] = useState<string | null>(null)
	const [showAddForm, setShowAddForm] = useState(false)

	const { data: patient } = trpc.patient.getByFilter.useQuery({ where: {} })
	const { data: addresses, isLoading } = trpc.address.getAllByFilter.useQuery(
		{ where: { patientId: patient?.id } },
		{ enabled: !!patient?.id },
	)

	function handleSuccess() {
		setEditingId(null)
		setShowAddForm(false)
		toast.success(t("address.saveSuccess"))
	}

	return (
		<Page>
			<Container width={"default"} padding={"lg"} gap={"lg"}>
				<PageHeader title={t("address.title")} />

				{isLoading ? (
					<p className="text-muted-foreground">{t("common.loading")}</p>
				) : addresses && addresses.length > 0 ? (
					addresses.map((address: Address) => (
						<Card key={address.id}>
							<CardHeader className="flex flex-row items-center justify-between pb-2">
								<CardTitle className="capitalize">{address.type}</CardTitle>
								{editingId !== address.id && (
									<Button
										variant="outline"
										size="sm"
										onClick={() => {
											setShowAddForm(false)
											setEditingId(address.id)
										}}
									>
										{t("common.edit")}
									</Button>
								)}
							</CardHeader>
							<CardContent>
								{editingId === address.id ? (
									<AddressForm entity={address} onSuccess={handleSuccess} />
								) : (
									<div className="text-sm space-y-1">
										<p>{address.line1}</p>
										{address.line2 && <p>{address.line2}</p>}
										<p>
											{address.city}
											{address.state ? `, ${address.state}` : ""}{" "}
											{address.postalCode}
										</p>
										<p>{address.country}</p>
									</div>
								)}
							</CardContent>
						</Card>
					))
				) : (
					<p className="text-muted-foreground">{t("address.noAddresses")}</p>
				)}

				{showAddForm ? (
					<Card>
						<CardHeader>
							<CardTitle>{t("address.addAddress")}</CardTitle>
						</CardHeader>
						<CardContent>
							<AddressForm patientId={patient?.id} onSuccess={handleSuccess} />
						</CardContent>
					</Card>
				) : (
					<Button
						variant="outline"
						onClick={() => {
							setEditingId(null)
							setShowAddForm(true)
						}}
					>
						{t("address.addAddress")}
					</Button>
				)}
			</Container>
		</Page>
	)
}
